import { Alert, Box, Button, Group, Modal, Stack, Stepper, Text, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import type { Bundle, Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { Document, Loading, QuestionnaireForm } from '@medplum/react';
import type { AppointmentTypeDefinition } from '@polarissleep/scheduling';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { trackAppointmentBooked } from '../analytics/funnel';
import { AppointmentTimeWithTimezone } from '../components/appointments/AppointmentTimeWithTimezone';
import type { ConsentRelationship } from '../components/InformedConsentModal';
import { type IntakeSelection, IntakeSlotPicker } from '../components/onboarding/IntakeSlotPicker';
import SleepHistoryContent from '../components/onboarding/SleepHistoryContent';
import { getConfig } from '../config';
import { getIntakeMedplumClient } from '../lib/bot-client';
import { botIdentifier } from '../lib/bot-identifier';
import { stripNoneOfTheAboveIfSiblings } from '../lib/exclusion-cleanup';
import {
  clearIntakeConsentSignature,
  getIntakeConsentAgreement,
  getIntakeConsentRelationship,
  getIntakeConsentSignature,
} from '../lib/intake-consent-signature';
import { formatAppointmentDate } from '../utils/appointment-formatting';
import { getProviderAppointmentUrl } from '../utils/provider-appointment-url';

const ONBOARDING_STEPS = [
  { label: 'About You', description: 'Name & Contact Info' },
  { label: 'Schedule Intake', description: 'Meet Your Team' },
  { label: 'Next Steps', description: 'Get Your Care Plan' },
];

const QUESTIONNAIRE_PAGES = ['eligibility-check-page-1', 'sleep-history'] as const;

type PageState =
  | 'loading'
  | 'error'
  | 'eligibility-confirmation'
  | 'eligibility-check-page-1'
  | 'sleep-history'
  | 'eligibility-check-finished'
  | 'eligibility-appointment-pending'
  | 'eligibility-appointment-booked';

function getStepperActive(state: PageState): number {
  if (state === 'eligibility-check-finished' || state === 'eligibility-appointment-booked') {
    return 3;
  }
  if (state === 'eligibility-appointment-pending') {
    return 2;
  }
  return 1;
}

interface BookedAppointmentInfo {
  appointmentId: string;
  patientId: string;
  start: string;
  end: string;
  providerName: string;
  mode: AppointmentTypeDefinition['mode'];
}

export function EligibilityPage(): JSX.Element {
  const { uniqueId } = useParams<{ uniqueId: string }>();
  const isSmallScreen = useMediaQuery('(max-width: 876px)');
  const [pageState, setPageState] = useState<PageState>('loading');
  const [submitting, setSubmitting] = useState(false);
  const [questionnaires, setQuestionnaires] = useState<Map<string, Questionnaire>>(new Map());
  const [bookedAppointment, setBookedAppointment] = useState<BookedAppointmentInfo | undefined>();
  const [questionnaireChoiceOpen, setQuestionnaireChoiceOpen] = useState(false);
  // Which route the patient took into 'eligibility-check-finished'. Drives the Next Steps
  // copy on SuccessScreen. Session-only: a patient who reloads onto the finished phase gets
  // the completed copy, which is the safer default of the two.
  const [questionnaireDeferred, setQuestionnaireDeferred] = useState(false);
  const [informedConsentSignature, setInformedConsentSignature] = useState<string | undefined>();
  const [informedConsentPdf, setInformedConsentPdf] = useState<string | undefined>();
  const [informedConsentRelationship, setInformedConsentRelationship] = useState<ConsentRelationship | undefined>();
  const confirmationSkipAttemptedFor = useRef<string | undefined>(undefined);

  // Fetch current phase and load questionnaires
  useEffect(() => {
    if (!uniqueId) {
      setPageState('error');
      return;
    }

    setInformedConsentSignature(getIntakeConsentSignature(uniqueId));
    setInformedConsentPdf(getIntakeConsentAgreement(uniqueId));
    setInformedConsentRelationship(getIntakeConsentRelationship(uniqueId) as ConsentRelationship | undefined);

    Promise.all([
      getIntakeMedplumClient().then((client) =>
        client.executeBot(botIdentifier('onboarding-status'), { uniqueId }, 'application/json')
      ),
      fetch('/public-questionnaires.json').then((res) => res.json()),
    ])
      .then(
        ([phaseData, bundle]: [
          {
            phase: string;
            bookedAppointment?: BookedAppointmentInfo;
          },
          Bundle,
        ]) => {
          // Load questionnaires
          const loaded = new Map<string, Questionnaire>();
          for (const entry of bundle.entry ?? []) {
            const resource = entry.resource;
            if (resource?.resourceType === 'Questionnaire' && resource.name) {
              loaded.set(resource.name, resource);
            }
          }
          setQuestionnaires(loaded);
          if (phaseData.bookedAppointment) {
            setBookedAppointment(phaseData.bookedAppointment);
          }

          // Set page state from phase
          const phase = phaseData.phase as PageState;
          if (
            phase === 'eligibility-confirmation' ||
            phase === 'eligibility-check-page-1' ||
            phase === 'sleep-history' ||
            phase === 'eligibility-check-finished' ||
            phase === 'eligibility-appointment-pending' ||
            phase === 'eligibility-appointment-booked'
          ) {
            setPageState(phase);
          } else {
            setPageState('error');
          }
        }
      )
      .catch((error) => {
        Sentry.captureException(error);
        setPageState('error');
      });
  }, [uniqueId]);

  // Intake is forward-only, including browser history navigation.
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    const preventBackNavigation = (): void => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', preventBackNavigation);
    return () => window.removeEventListener('popstate', preventBackNavigation);
  }, []);

  const advancePhase = useCallback(async () => {
    if (!uniqueId) {
      return;
    }
    // Re-fetch the current phase to render next page
    try {
      const client = await getIntakeMedplumClient();
      const data = await client.executeBot(botIdentifier('onboarding-status'), { uniqueId }, 'application/json');
      setPageState((data as { phase: string }).phase as PageState);
    } catch (error) {
      Sentry.captureException(error);
      showNotification({
        color: 'red',
        message: 'Failed to load next page. Please refresh.',
        autoClose: false,
      });
    }
  }, [uniqueId]);

  const handleConfirmationContinue = useCallback(async () => {
    if (!uniqueId) {
      return;
    }

    setSubmitting(true);
    try {
      await Sentry.startSpan(
        {
          op: 'bot.execute',
          name: 'Execute process-eligibility-step (confirmation)',
        },
        async (span) => {
          span?.setAttribute('eligibilityChoice', 'appointment');
          const client = await getIntakeMedplumClient();
          await client.executeBot(
            botIdentifier('process-eligibility-step'),
            {
              uniqueId,
              phase: 'eligibility-confirmation',
              eligibilityChoice: 'appointment',
              ...(informedConsentSignature ? { signature: informedConsentSignature } : {}),
              ...(informedConsentPdf ? { agreementPdfBase64: informedConsentPdf } : {}),
              ...(informedConsentRelationship ? { consentRelationship: informedConsentRelationship } : {}),
            },
            'application/json'
          );
        }
      );

      if (informedConsentSignature) {
        clearIntakeConsentSignature(uniqueId);
        setInformedConsentSignature(undefined);
        setInformedConsentRelationship(undefined);
      }

      await advancePhase();
    } catch (error) {
      Sentry.captureException(error);
      showNotification({
        color: 'red',
        message: 'Failed to submit. Please try again.',
        autoClose: false,
      });
    } finally {
      setSubmitting(false);
    }
  }, [uniqueId, informedConsentSignature, informedConsentPdf, informedConsentRelationship, advancePhase]);

  // The confirmation phase is retained by the backend for workflow compatibility,
  // but it is no longer shown to patients. Advance automatically to scheduling.
  useEffect(() => {
    if (
      pageState !== 'eligibility-confirmation' ||
      submitting ||
      !uniqueId ||
      confirmationSkipAttemptedFor.current === uniqueId
    ) {
      return;
    }

    confirmationSkipAttemptedFor.current = uniqueId;
    handleConfirmationContinue().catch(console.error);
  }, [pageState, submitting, uniqueId, handleConfirmationContinue]);

  const handleQuestionnaireNow = useCallback(async (): Promise<void> => {
    if (!uniqueId) {
      return;
    }
    setSubmitting(true);
    try {
      await Sentry.startSpan({ op: 'bot.execute', name: 'Execute start post-booking questionnaire' }, async () => {
        const client = await getIntakeMedplumClient();
        await client.executeBot(
          botIdentifier('process-eligibility-step'),
          { uniqueId, phase: 'eligibility-appointment-booked', sendWelcomeEmail: true },
          'application/json'
        );
      });
      setQuestionnaireChoiceOpen(false);
      setQuestionnaireDeferred(false);
      setPageState('eligibility-check-page-1');
    } catch (error) {
      Sentry.captureException(error);
      showNotification({ color: 'red', message: 'Failed to start questionnaire. Please try again.', autoClose: false });
    } finally {
      setSubmitting(false);
    }
  }, [uniqueId]);

  const handleQuestionnaireLater = useCallback(async (): Promise<void> => {
    if (!uniqueId) {
      return;
    }
    setSubmitting(true);
    try {
      const client = await getIntakeMedplumClient();
      await client.executeBot(
        botIdentifier('process-eligibility-step'),
        { uniqueId, phase: 'eligibility-appointment-booked', deferQuestionnaire: true, sendWelcomeEmail: true },
        'application/json'
      );
      setQuestionnaireDeferred(true);
      setQuestionnaireChoiceOpen(false);
      // Deferring ends the intake: go straight to the success screen, which carries the
      // "your questionnaire is waiting in the portal" copy. The bot intentionally leaves the
      // patient's phase at 'eligibility-appointment-booked' so the deferred questionnaire is
      // still outstanding, so this transition is client-side only.
      setPageState('eligibility-check-finished');
    } catch (error) {
      Sentry.captureException(error);
      showNotification({ color: 'red', message: 'Failed to save your choice. Please try again.', autoClose: false });
    } finally {
      setSubmitting(false);
    }
  }, [uniqueId]);

  // Handle slot-picker booking submission
  const handleBookSlot = useCallback(
    async (slot: IntakeSelection): Promise<void> => {
      if (!uniqueId) {
        return;
      }
      setSubmitting(true);
      try {
        const client = await getIntakeMedplumClient();
        const result = await Sentry.startSpan({ op: 'bot.execute', name: 'Execute book-intake-slot' }, async () => {
          return (await client.executeBot(
            botIdentifier('book-intake-slot'),
            {
              uniqueId,
              appointment: slot.appointment,
            },
            'application/json'
          )) as BookedAppointmentInfo;
        });
        trackAppointmentBooked(uniqueId);
        setBookedAppointment(result);
        setQuestionnaireChoiceOpen(true);
        setPageState('eligibility-appointment-booked');
      } catch (error) {
        Sentry.captureException(error);
        const message = error instanceof Error ? error.message : 'Failed to book appointment. Please try again.';
        showNotification({ color: 'red', message, autoClose: false });
      } finally {
        setSubmitting(false);
      }
    },
    [uniqueId]
  );

  // Handle questionnaire page submission (pages 1-4)
  const handleQuestionnaireSubmit = useCallback(
    async (response: QuestionnaireResponse) => {
      if (!uniqueId) {
        return;
      }

      // Mutual-exclusion cleanup for the HSAT exclusion checklist on page 1.
      // If the patient ticked both "None of the above" and a real exclusion
      // (misclick / "I only have this one" gesture), the real exclusion wins
      // and "none" is stripped before the bot sees the QR.
      const cleanedResponse =
        pageState === 'eligibility-check-page-1'
          ? stripNoneOfTheAboveIfSiblings(stripNoneOfTheAboveIfSiblings(response, 'diagnoses'), 'clinical-conditions')
          : response;

      setSubmitting(true);
      try {
        await Sentry.startSpan(
          {
            op: 'bot.execute',
            name: `Execute process-eligibility-step (${pageState})`,
          },
          async () => {
            const client = await getIntakeMedplumClient();
            await client.executeBot(
              botIdentifier('process-eligibility-step'),
              {
                uniqueId,
                phase: pageState,
                questionnaireResponse: cleanedResponse,
              },
              'application/json'
            );
          }
        );

        await advancePhase();
      } catch (error) {
        Sentry.captureException(error);
        showNotification({
          color: 'red',
          message: 'Failed to submit. Please try again.',
          autoClose: false,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [uniqueId, pageState, advancePhase]
  );

  const handleSleepHistorySubmit = useCallback(
    async (essQR: QuestionnaireResponse, stopBangQR: QuestionnaireResponse): Promise<void> => {
      if (!uniqueId) {
        return;
      }
      setSubmitting(true);
      try {
        await Sentry.startSpan(
          { op: 'bot.execute', name: 'Execute process-eligibility-step (sleep-history)' },
          async () => {
            const client = await getIntakeMedplumClient();
            await client.executeBot(
              botIdentifier('process-eligibility-step'),
              {
                uniqueId,
                phase: 'sleep-history',
                questionnaireResponseEpworth: essQR,
                questionnaireResponseStopBang: stopBangQR,
              },
              'application/json'
            );
          }
        );
        await advancePhase();
      } catch (error) {
        Sentry.captureException(error);
        showNotification({
          color: 'red',
          message: 'Failed to submit. Please try again.',
          autoClose: false,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [uniqueId, advancePhase]
  );

  if (pageState === 'loading') {
    return <Loading />;
  }

  if (pageState === 'error') {
    return (
      <>
        <img
          src="/Logo-Black.png"
          alt="PolarisSleep"
          style={{ display: 'block', margin: '24px auto 16px', width: 171 }}
        />
        <Document width={800} bg={'var(--mantine-color-white)'}>
          <Alert color="red" title="Page not found">
            This onboarding link is invalid or has expired. Please contact support.
          </Alert>
        </Document>
      </>
    );
  }

  const stepperActive = getStepperActive(pageState);

  return (
    <>
      <img
        src="/Logo-Black.png"
        alt="PolarisSleep"
        style={{ display: 'block', margin: '24px auto 16px', width: 171 }}
      />
      <Document width={800} bg={'var(--mantine-color-white)'}>
        <Title order={2} mb="md" ta="center">
          New Patient Intake Form
        </Title>

        <Box
          style={{
            display: isSmallScreen ? 'flex' : 'block',
            justifyContent: 'center',
            border: '1px solid var(--mantine-color-gray-0)',
            borderRadius: 'var(--mantine-radius-md)',
          }}
          bg="gray.0"
        >
          <Stepper active={stepperActive} orientation={isSmallScreen ? 'vertical' : 'horizontal'} px={'xl'} py={'lg'}>
            {ONBOARDING_STEPS.map((step) => (
              <Stepper.Step key={step.label} label={step.label} description={step.description} />
            ))}
          </Stepper>
        </Box>
      </Document>

      <Document width={800} bg={'var(--mantine-color-white)'} px="xl">
        {pageState === 'eligibility-confirmation' && <Loading />}

        {pageState === 'eligibility-check-page-1' && (
          <QuestionnairePageContent
            pageState={pageState}
            questionnaires={questionnaires}
            submitting={submitting}
            onSubmit={handleQuestionnaireSubmit}
          />
        )}

        {pageState === 'sleep-history' && (
          <SleepHistoryContent submitting={submitting} onSubmit={handleSleepHistorySubmit} />
        )}

        {pageState === 'eligibility-appointment-pending' && uniqueId && (
          <IntakeSchedulingContent uniqueId={uniqueId} submitting={submitting} onBook={handleBookSlot} />
        )}

        {pageState === 'eligibility-appointment-booked' && bookedAppointment && (
          <>
            <AppointmentBookedScreen booking={bookedAppointment} />
            <Modal
              opened={questionnaireChoiceOpen}
              onClose={() => setQuestionnaireChoiceOpen(false)}
              title="Sleep History Questionnaire"
              centered
            >
              <Text mb="md">
                We have a few more questions for you to help us understand your sleep history. It will take about 2–3
                minutes to complete.
              </Text>
              <Text mb="lg">Would you like to fill it out now or later?</Text>
              <Group justify="flex-end">
                <Button
                  variant="subtle"
                  onClick={() => handleQuestionnaireLater().catch(console.error)}
                  disabled={submitting}
                >
                  Later
                </Button>
                <Button onClick={() => handleQuestionnaireNow().catch(console.error)} loading={submitting}>
                  Now
                </Button>
              </Group>
            </Modal>
          </>
        )}

        {pageState === 'eligibility-check-finished' && (
          <SuccessScreen booking={bookedAppointment} deferred={questionnaireDeferred} />
        )}
      </Document>
    </>
  );
}

// --- Sub-components ---

interface QuestionnairePageContentProps {
  pageState: (typeof QUESTIONNAIRE_PAGES)[number];
  questionnaires: Map<string, Questionnaire>;
  submitting: boolean;
  onSubmit: (response: QuestionnaireResponse) => void;
}

function QuestionnairePageContent({
  pageState,
  questionnaires,
  submitting,
  onSubmit,
}: QuestionnairePageContentProps): JSX.Element {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const questionnaire = questionnaires.get(pageState);

  if (!questionnaire) {
    return (
      <Alert color="red" title="Questionnaire not found">
        The questionnaire for this step could not be found. Please refresh the page or contact support.
      </Alert>
    );
  }

  let submitLabel = 'Continue';
  if (submitting) {
    submitLabel = 'Submitting…';
  }

  return (
    <>
      <Title order={2} ta="center" mb={4}>
        Medical history
      </Title>
      <Text ta="center" c="dimmed" mb="xl" size="lg">
        A few questions about your health. None of these will disqualify you from care — they help our clinicians review
        your intake.
      </Text>
      <Box px="xl">
        <div ref={formContainerRef}>
          <QuestionnaireForm key={pageState} questionnaire={questionnaire} onSubmit={onSubmit} excludeButtons={true} />
        </div>
        <Group justify="flex-end" mt="md">
          <Button
            loading={submitting}
            disabled={submitting}
            onClick={() => {
              formContainerRef.current?.querySelector('form')?.requestSubmit();
            }}
          >
            {submitLabel}
          </Button>
        </Group>
      </Box>
    </>
  );
}

interface IntakeSchedulingContentProps {
  uniqueId: string;
  submitting: boolean;
  onBook: (slot: IntakeSelection) => Promise<void>;
}

function IntakeSchedulingContent({ uniqueId, submitting, onBook }: IntakeSchedulingContentProps): JSX.Element {
  const [selectedSlot, setSelectedSlot] = useState<IntakeSelection | undefined>();

  return (
    <Stack gap="lg">
      <IntakeSlotPicker
        uniqueId={uniqueId}
        selectedSlot={selectedSlot}
        onSlotSelected={(slot) => setSelectedSlot(slot)}
      />
      <Group justify="flex-end" mt="md">
        <Button
          onClick={() => {
            if (selectedSlot) {
              onBook(selectedSlot).catch(console.error);
            }
          }}
          disabled={!selectedSlot || submitting}
          loading={submitting}
        >
          Submit
        </Button>
      </Group>
    </Stack>
  );
}

interface AppointmentBookedScreenProps {
  booking: BookedAppointmentInfo;
}

function AppointmentBookedScreen({ booking }: AppointmentBookedScreenProps): JSX.Element {
  return (
    <>
      <Title order={3} ta="center" mb={4}>
        Submission Successful!
      </Title>
      <Text ta="center" c="dimmed" mb="lg" maw={600} mx="auto">
        We have successfully received your information. We will send a confirmation to your preferred contact method.
      </Text>
      <Text ta="center" c="dimmed" mb="xl">
        Be on the look out for a welcome email.
      </Text>

      <Box
        mx="auto"
        maw={420}
        p="md"
        mb="xl"
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 'var(--mantine-radius-md)',
          backgroundColor: 'var(--mantine-color-gray-0)',
        }}
      >
        <Stack gap={6} align="center">
          <Text size="xs" c="dimmed">
            Your Upcoming Appointment
          </Text>
          <Text size="md" fw={600}>
            {formatAppointmentDate(booking.start)}
          </Text>
          <Box
            mt={4}
            px="md"
            py={4}
            style={{
              backgroundColor: 'var(--mantine-color-polaris-0)',
              borderRadius: 'var(--mantine-radius-sm)',
            }}
          >
            <AppointmentTimeWithTimezone
              start={booking.start}
              end={booking.end}
              groupProps={{ justify: 'center' }}
              textProps={{ size: 'sm', fw: 500, c: 'polaris.7' }}
            />
          </Box>
        </Stack>
      </Box>

      <Group justify="center" mt="xl">
        <Button onClick={() => window.close()}>Close</Button>
      </Group>
    </>
  );
}

interface SuccessScreenProps {
  booking?: BookedAppointmentInfo;
  deferred: boolean;
}

function SuccessScreen({ booking, deferred }: SuccessScreenProps): JSX.Element {
  return (
    <>
      <Title order={3} ta="center" mb={4}>
        Submission Successful!
      </Title>
      <Text ta="center" c="dimmed" mb="xl">
        We have successfully received your information.
      </Text>

      <Title order={4} ta="center" mb="sm">
        Next Steps:
      </Title>
      <Text ta="center" c="dimmed" mb="lg" maw={600} mx="auto">
        {deferred
          ? "We'll send you a welcome email to set up your patient portal. Once you log in, you'll find your sleep history questionnaire waiting there — please complete it before your appointment. A physician will review it once it's in."
          : "We'll send you a welcome email to set up your patient portal, where you can review everything you've shared. A physician will review your questionnaire before your appointment."}
      </Text>

      {booking && (
        <Box
          mx="auto"
          maw={420}
          p="md"
          style={{
            border: '1px solid var(--mantine-color-gray-3)',
            borderRadius: 'var(--mantine-radius-md)',
            backgroundColor: 'var(--mantine-color-gray-0)',
          }}
        >
          <Stack gap={6} align="center">
            <Text size="xs" c="dimmed">
              Your Appointment
            </Text>
            <Text size="md" fw={600}>
              {formatAppointmentDate(booking.start)}
            </Text>
            <AppointmentTimeWithTimezone
              start={booking.start}
              end={booking.end}
              groupProps={{ justify: 'center' }}
              textProps={{ size: 'sm', fw: 500, c: 'polaris.7' }}
            />
          </Stack>
        </Box>
      )}
    </>
  );
}
